import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import useAccountStatements from 'features/myVaro/hooks/statementsAndForms/useAccountStatements';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import usePayOverTimeLoanStatements from 'features/payOverTime/hooks/usePayOverTimeLoanStatements';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { FlatList, StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { useStatsig } from 'shared/hooks/statsig/useStatsig';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { FeatureFlagKey } from 'shared/providers/statsig/StatsigProvider.d';
import theme from 'shared/styles/theme';
import useVaroUserFlow from 'shared/utils/useVaroUserFlow/useVaroUserFlow';

const AccountStatementsScreen = () => {
  const router = useRouter();
  const flow = useVaroUserFlow();
  const { isFeatureEnabled } = useStatsig();
  const { setYearSelectionScreenTitle } = useContext(MyVaroContext);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'statementsAndForms',
  });

  const {
    accountStatementsDetails,
    closedStatementsDetails,
    errorModel,
    loading,
  } = useAccountStatements({
    accountId: '',
    requestPolicy: 'cache-and-network',
  });
  const isPayOverTimeEnabled = isFeatureEnabled(
    FeatureFlagKey.ENABLE_PAY_OVER_TIME,
  );
  const {
    statements: payOverTimeStatements,
    errorModel: payOverTimeStatementsErrorModel,
    loading: payOverTimeStatementsLoading,
  } = usePayOverTimeLoanStatements('cache-and-network', !isPayOverTimeEnabled);
  const hasPayOverTimeStatements =
    isPayOverTimeEnabled &&
    !payOverTimeStatementsLoading &&
    !payOverTimeStatementsErrorModel &&
    payOverTimeStatements.length > 0;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: translatedText('accountStatements.title'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <FlatList
        data={accountStatementsDetails}
        renderItem={({ item }) => (
          <ListItem
            key={item.id}
            primaryCopy={`${item.name} - ${item.accountNumber ?? ''}`}
            onPress={() => {
              setYearSelectionScreenTitle?.(item.name);

              router.push({
                pathname: `/${flow}/statements-and-forms/account-statements/[id]`,
                params: { id: item.id },
              });
            }}
          />
        )}
        ItemSeparatorComponent={() => (
          <View style={styles.hairLineDivider}>
            <HairLineDivider />
          </View>
        )}
        keyExtractor={(item) => item?.id}
        removeClippedSubviews={false}
        ListFooterComponent={
          <>
            {hasPayOverTimeStatements ? (
              <>
                <View style={styles.hairLineDivider}>
                  <HairLineDivider />
                </View>
                <ListItem
                  primaryCopy={translatedText(
                    'payOverTimeStatements.accountTitle',
                  )}
                  onPress={() => {
                    router.push({
                      pathname: `/${flow}/statements-and-forms/pay-over-time-statements`,
                    });
                  }}
                />
              </>
            ) : null}
            {closedStatementsDetails?.length ? (
              <>
                <View style={styles.hairLineDivider}>
                  <HairLineDivider />
                </View>
                <ListItem
                  primaryCopy={translatedText('closedAccounts.title')}
                  onPress={() => {
                    router.push({
                      pathname: `/${flow}/statements-and-forms/account-statements/closed-accounts`,
                    });
                  }}
                />
              </>
            ) : null}
          </>
        }
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  hairLineDivider: { marginHorizontal: theme.spacing.m },
});

export default AccountStatementsScreen;
